import React, { useState } from 'react';
import { 
  Building2, User, Briefcase, FileText, CheckCircle, 
  ChevronRight, ChevronLeft, Send, AlertCircle,
  Calendar, MapPin, Phone, Mail, Upload, X
} from 'lucide-react';
import axios from 'axios';

const TirociniForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Step 1: Dati Contraente
    contraente: '',
    pivaCfContraente: '',
    referenteContatto: '',
    emailComunicazioni: '',
    telefonoContatto: '',
    
    // Step 2: Tipologia e Tirocinante
    tipologiaTirocinio: '',
    tipologiaTirocinioAltro: '',
    tirocinanteCognomeNome: '',
    tirocinanteCodiceFiscale: '',
    tirocinanteNatoA: '',
    tirocinanteDataNascita: '',
    tirocinanteResidenza: '',
    
    // Step 3: Azienda Ospitante
    aziendaDenominazione: '',
    aziendaSede: '',
    aziendaSettore: '',
    aziendaPiva: '',
    
    // Step 4: Dettagli Tirocinio
    tipologiaMansione: '',
    descrizioneAttivita: '',
    dataInizio: '',
    dataFine: '',
    consensoPrivacy: false,
    visuraEnte: null,
    docIdentita: null
  });

  const totalSteps = 4;

  const tipologieTirocinio = [
    { value: 'agenzia_mediazione', label: 'Agenzia di Mediazione' },
    { value: 'includis', label: 'Includis' },
    { value: 'plus21', label: 'Plus21' },
    { value: 'gol', label: 'GOL' },
    { value: 'altro', label: 'Altri (specificare)' }
  ];

  const tipologieMansione = [
    { value: 'amministrativa', label: 'Solo amministrativa' },
    { value: 'amministrativa_manuale', label: 'Amministrativa e manuale' },
    { value: 'manuale', label: 'Solo manuale' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch(step) {
      case 1:
        if (!formData.contraente) newErrors.contraente = 'Campo obbligatorio';
        if (!formData.referenteContatto) newErrors.referenteContatto = 'Campo obbligatorio';
        if (!formData.emailComunicazioni) newErrors.emailComunicazioni = 'Campo obbligatorio';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailComunicazioni)) newErrors.emailComunicazioni = 'Email non valida';
        if (!formData.telefonoContatto) newErrors.telefonoContatto = 'Campo obbligatorio';
        break;
      case 2:
        if (!formData.tipologiaTirocinio) newErrors.tipologiaTirocinio = 'Seleziona una tipologia';
        if (formData.tipologiaTirocinio === 'altro' && !formData.tipologiaTirocinioAltro) newErrors.tipologiaTirocinioAltro = 'Specifica la tipologia';
        if (!formData.tirocinanteCognomeNome) newErrors.tirocinanteCognomeNome = 'Campo obbligatorio';
        if (!formData.tirocinanteCodiceFiscale) newErrors.tirocinanteCodiceFiscale = 'Campo obbligatorio';
        if (!formData.tirocinanteResidenza) newErrors.tirocinanteResidenza = 'Campo obbligatorio';
        break;
      case 3:
        if (!formData.aziendaDenominazione) newErrors.aziendaDenominazione = 'Campo obbligatorio';
        if (!formData.aziendaSettore) newErrors.aziendaSettore = 'Campo obbligatorio';
        if (!formData.aziendaPiva) newErrors.aziendaPiva = 'Campo obbligatorio';
        break;
      case 4:
        if (!formData.tipologiaMansione) newErrors.tipologiaMansione = 'Seleziona una tipologia';
        if (!formData.descrizioneAttivita) newErrors.descrizioneAttivita = 'Campo obbligatorio';
        if (!formData.dataInizio) newErrors.dataInizio = 'Campo obbligatorio';
        if (!formData.dataFine) newErrors.dataFine = 'Campo obbligatorio';
        if (!formData.consensoPrivacy) newErrors.consensoPrivacy = 'Il consenso è obbligatorio per procedere';
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'visuraEnte' || key === 'docIdentita') {
          if (formData[key]) {
            submitData.append(key, formData[key]);
          }
        } else if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/tirocini`,
        submitData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.status === 'success') {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Errore invio:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepIcons = {
    1: Building2,
    2: User,
    3: Briefcase,
    4: FileText
  };

  const stepTitles = {
    1: 'Contraente',
    2: 'Tirocinante',
    3: 'Azienda',
    4: 'Dettagli'
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex justify-between items-center">
        {[1, 2, 3, 4].map((step) => {
          const Icon = stepIcons[step];
          const isActive = currentStep === step;
          const isCompleted = currentStep > step;
          
          return (
            <div key={step} className="flex-1 flex items-center">
              <div className="flex flex-col items-center w-full">
                <div 
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isActive 
                      ? 'bg-[#9B59B6] text-white shadow-lg scale-110' 
                      : isCompleted 
                        ? 'bg-[#5FB76E] text-white' 
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {isCompleted ? <CheckCircle size={20} /> : <Icon size={20} />}
                </div>
                <span className={`text-xs mt-2 text-center hidden sm:block ${
                  isActive ? 'text-[#9B59B6] font-semibold' : 'text-gray-500'
                }`}>
                  {stepTitles[step]}
                </span>
              </div>
              {step < 4 && (
                <div className={`flex-1 h-1 mx-2 rounded ${
                  currentStep > step ? 'bg-[#5FB76E]' : 'bg-gray-200'
                }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-[#9B59B6]/10 rounded-lg flex items-center justify-center">
          <Building2 className="text-[#9B59B6]" size={20} />
        </div>
        <h3 className="text-xl font-bold text-[#9B59B6]">Dati Contraente</h3>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contraente (Fondazione, APS, Coop, Altre...) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="contraente"
            value={formData.contraente}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.contraente ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#9B59B6] focus:border-transparent bg-white text-gray-900`}
            placeholder="Es. Cooperativa Sociale XYZ"
          />
          {errors.contraente && <p className="text-red-500 text-sm mt-1">{errors.contraente}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            P.IVA / Codice Fiscale <span className="text-gray-400 text-xs">(non compilare se gia cliente)</span>
          </label>
          <input
            type="text"
            name="pivaCfContraente"
            value={formData.pivaCfContraente}
            onChange={handleInputChange}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#9B59B6] focus:border-transparent bg-white text-gray-900"
            placeholder="Es. 12345678901"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Referente / Contatto <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              name="referenteContatto"
              value={formData.referenteContatto}
              onChange={handleInputChange}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border ${errors.referenteContatto ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#9B59B6] focus:border-transparent bg-white text-gray-900`}
              placeholder="Nome e Cognome"
            />
          </div>
          {errors.referenteContatto && <p className="text-red-500 text-sm mt-1">{errors.referenteContatto}</p>}
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email per comunicazioni <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="email"
                name="emailComunicazioni"
                value={formData.emailComunicazioni}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border ${errors.emailComunicazioni ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#9B59B6] focus:border-transparent bg-white text-gray-900`}
                placeholder="email@esempio.it"
              />
            </div>
            {errors.emailComunicazioni && <p className="text-red-500 text-sm mt-1">{errors.emailComunicazioni}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cellulare o Tel del Contatto <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="tel"
                name="telefonoContatto"
                value={formData.telefonoContatto}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border ${errors.telefonoContatto ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#9B59B6] focus:border-transparent bg-white text-gray-900`}
                placeholder="+39 333 000 0000"
              />
            </div>
            {errors.telefonoContatto && <p className="text-red-500 text-sm mt-1">{errors.telefonoContatto}</p>}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-[#3AAFA9]/10 rounded-lg flex items-center justify-center">
          <User className="text-[#3AAFA9]" size={20} />
        </div>
        <h3 className="text-xl font-bold text-[#3AAFA9]">Tipologia e Dati Tirocinante</h3>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipologia Tirocinio <span className="text-red-500">*</span>
          </label>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {tipologieTirocinio.map(tipo => (
              <label 
                key={tipo.value}
                className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.tipologiaTirocinio === tipo.value
                    ? 'border-[#3AAFA9] bg-[#3AAFA9]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="tipologiaTirocinio"
                  value={tipo.value}
                  checked={formData.tipologiaTirocinio === tipo.value}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <span className={`text-sm ${formData.tipologiaTirocinio === tipo.value ? 'font-semibold text-[#3AAFA9]' : 'text-gray-700'}`}>
                  {tipo.label}
                </span>
              </label>
            ))}
          </div>
          {errors.tipologiaTirocinio && <p className="text-red-500 text-sm mt-1">{errors.tipologiaTirocinio}</p>}
        </div>
        
        {formData.tipologiaTirocinio === 'altro' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specifica tipologia <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="tipologiaTirocinioAltro"
              value={formData.tipologiaTirocinioAltro}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.tipologiaTirocinioAltro ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent bg-white text-gray-900`}
              placeholder="Indica la tipologia..."
            />
            {errors.tipologiaTirocinioAltro && <p className="text-red-500 text-sm mt-1">{errors.tipologiaTirocinioAltro}</p>}
          </div>
        )}
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tirocinante: Cognome e Nome <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="tirocinanteCognomeNome"
              value={formData.tirocinanteCognomeNome}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.tirocinanteCognomeNome ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent bg-white text-gray-900`}
              placeholder="Rossi Mario"
            />
            {errors.tirocinanteCognomeNome && <p className="text-red-500 text-sm mt-1">{errors.tirocinanteCognomeNome}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tirocinante: Codice Fiscale <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="tirocinanteCodiceFiscale"
              value={formData.tirocinanteCodiceFiscale}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.tirocinanteCodiceFiscale ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent bg-white text-gray-900 uppercase`}
              placeholder="RSSMRA80A01H501U"
              maxLength={16}
            />
            {errors.tirocinanteCodiceFiscale && <p className="text-red-500 text-sm mt-1">{errors.tirocinanteCodiceFiscale}</p>}
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tirocinante: Nato a <span className="text-gray-400 text-xs">(opzionale)</span>
            </label>
            <input
              type="text"
              name="tirocinanteNatoA"
              value={formData.tirocinanteNatoA}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent bg-white text-gray-900"
              placeholder="Es. Roma"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tirocinante: Data di Nascita <span className="text-gray-400 text-xs">(opzionale)</span>
            </label>
            <input
              type="date"
              name="tirocinanteDataNascita"
              value={formData.tirocinanteDataNascita}
              onChange={handleInputChange}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent bg-white text-gray-900"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tirocinante: Residenza completa <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3 text-gray-400" size={18} />
            <textarea
              name="tirocinanteResidenza"
              value={formData.tirocinanteResidenza}
              onChange={handleInputChange}
              rows={2}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border ${errors.tirocinanteResidenza ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#3AAFA9] focus:border-transparent bg-white text-gray-900`}
              placeholder="Indirizzo, n. civico, CAP e Citta"
            />
          </div>
          {errors.tirocinanteResidenza && <p className="text-red-500 text-sm mt-1">{errors.tirocinanteResidenza}</p>}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-[#E67E22]/10 rounded-lg flex items-center justify-center">
          <Briefcase className="text-[#E67E22]" size={20} />
        </div>
        <h3 className="text-xl font-bold text-[#E67E22]">Azienda Ospitante</h3>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Denominazione Azienda <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="aziendaDenominazione"
            value={formData.aziendaDenominazione}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.aziendaDenominazione ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#E67E22] focus:border-transparent bg-white text-gray-900`}
            placeholder="Nome dell'azienda ospitante"
          />
          {errors.aziendaDenominazione && <p className="text-red-500 text-sm mt-1">{errors.aziendaDenominazione}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sede Operativa <span className="text-gray-400 text-xs">(dove il tirocinante svolgera l'incarico)</span>
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3 text-gray-400" size={18} />
            <textarea
              name="aziendaSede"
              value={formData.aziendaSede}
              onChange={handleInputChange}
              rows={2}
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#E67E22] focus:border-transparent bg-white text-gray-900"
              placeholder="Indirizzo, n. civico, CAP e Citta"
            />
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Settore Merceologico <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="aziendaSettore"
              value={formData.aziendaSettore}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.aziendaSettore ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#E67E22] focus:border-transparent bg-white text-gray-900`}
              placeholder="Es. Commercio, Servizi, Manifattura..."
            />
            {errors.aziendaSettore && <p className="text-red-500 text-sm mt-1">{errors.aziendaSettore}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              P.IVA Azienda <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="aziendaPiva"
              value={formData.aziendaPiva}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 rounded-lg border ${errors.aziendaPiva ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#E67E22] focus:border-transparent bg-white text-gray-900`}
              placeholder="12345678901"
            />
            {errors.aziendaPiva && <p className="text-red-500 text-sm mt-1">{errors.aziendaPiva}</p>}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-[#5FB76E]/10 rounded-lg flex items-center justify-center">
          <FileText className="text-[#5FB76E]" size={20} />
        </div>
        <h3 className="text-xl font-bold text-[#5FB76E]">Dettagli Tirocinio</h3>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tipologia di Mansione <span className="text-red-500">*</span>
          </label>
          <div className="grid sm:grid-cols-3 gap-3">
            {tipologieMansione.map(tipo => (
              <label 
                key={tipo.value}
                className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.tipologiaMansione === tipo.value
                    ? 'border-[#5FB76E] bg-[#5FB76E]/5'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="tipologiaMansione"
                  value={tipo.value}
                  checked={formData.tipologiaMansione === tipo.value}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <span className={`text-sm ${formData.tipologiaMansione === tipo.value ? 'font-semibold text-[#5FB76E]' : 'text-gray-700'}`}>
                  {tipo.label}
                </span>
              </label>
            ))}
          </div>
          {errors.tipologiaMansione && <p className="text-red-500 text-sm mt-1">{errors.tipologiaMansione}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Descrizione delle attivita previste dal progetto <span className="text-red-500">*</span>
          </label>
          <textarea
            name="descrizioneAttivita"
            value={formData.descrizioneAttivita}
            onChange={handleInputChange}
            rows={4}
            className={`w-full px-4 py-3 rounded-lg border ${errors.descrizioneAttivita ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#5FB76E] focus:border-transparent bg-white text-gray-900`}
            placeholder="Descrivi le attivita che il tirocinante svolgera..."
          />
          {errors.descrizioneAttivita && <p className="text-red-500 text-sm mt-1">{errors.descrizioneAttivita}</p>}
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Inizio Tirocinio <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="date"
                name="dataInizio"
                value={formData.dataInizio}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border ${errors.dataInizio ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#5FB76E] focus:border-transparent bg-white text-gray-900`}
              />
            </div>
            {errors.dataInizio && <p className="text-red-500 text-sm mt-1">{errors.dataInizio}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data Fine Tirocinio <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="date"
                name="dataFine"
                value={formData.dataFine}
                onChange={handleInputChange}
                className={`w-full pl-10 pr-4 py-3 rounded-lg border ${errors.dataFine ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-[#5FB76E] focus:border-transparent bg-white text-gray-900`}
              />
            </div>
            {errors.dataFine && <p className="text-red-500 text-sm mt-1">{errors.dataFine}</p>}
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visura Ente <span className="text-gray-400 text-xs">(ETS, Coop Soc, ecc...)</span>
            </label>
            <div className="relative">
              <input
                type="file"
                name="visuraEnte"
                onChange={handleInputChange}
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                id="visura-upload"
              />
              <label
                htmlFor="visura-upload"
                className="flex items-center justify-center w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#5FB76E] transition-colors"
              >
                {formData.visuraEnte ? (
                  <div className="flex items-center space-x-2">
                    <FileText className="text-[#5FB76E]" size={20} />
                    <span className="text-gray-700 text-sm truncate max-w-[150px]">{formData.visuraEnte.name}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setFormData(prev => ({ ...prev, visuraEnte: null }));
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="text-gray-400 mb-1" size={24} />
                    <span className="text-gray-500 text-xs text-center">Visura Camerale Ente</span>
                  </div>
                )}
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Doc. Identita Legale Rappresentante
            </label>
            <div className="relative">
              <input
                type="file"
                name="docIdentita"
                onChange={handleInputChange}
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                id="doc-identita-upload"
              />
              <label
                htmlFor="doc-identita-upload"
                className="flex items-center justify-center w-full px-4 py-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#5FB76E] transition-colors"
              >
                {formData.docIdentita ? (
                  <div className="flex items-center space-x-2">
                    <FileText className="text-[#5FB76E]" size={20} />
                    <span className="text-gray-700 text-sm truncate max-w-[150px]">{formData.docIdentita.name}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setFormData(prev => ({ ...prev, docIdentita: null }));
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="text-gray-400 mb-1" size={24} />
                    <span className="text-gray-500 text-xs text-center">Documento Identita</span>
                  </div>
                )}
              </label>
            </div>
          </div>
        </div>

        {/* Consenso Privacy Obbligatorio */}
        <div className={`p-5 rounded-xl border-2 ${errors.consensoPrivacy ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              data-testid="tirocini-privacy-checkbox"
              type="checkbox"
              name="consensoPrivacy"
              checked={formData.consensoPrivacy}
              onChange={handleInputChange}
              className="mt-1 w-5 h-5 text-[#5FB76E] rounded focus:ring-[#5FB76E]"
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              Dichiaro di aver preso visione dell'<a href="/privacy-policy" className="text-[#2B7A9F] underline" target="_blank" rel="noopener noreferrer">Informativa Privacy</a> e acconsento al trattamento dei dati personali (inclusi i documenti allegati) ai sensi del Regolamento UE 2016/679 (GDPR), per la sola finalità di gestione della presente richiesta. <span className="text-red-500">*</span>
            </span>
          </label>
          {errors.consensoPrivacy && <p className="text-red-500 text-sm mt-2">{errors.consensoPrivacy}</p>}
        </div>
      </div>
    </div>
  );

  if (submitStatus === 'success') {
    return (
      <div className="text-center py-12 animate-fade-in">
        <div className="w-20 h-20 bg-[#5FB76E]/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="text-[#5FB76E]" size={40} />
        </div>
        <h3 className="text-2xl font-bold text-[#5FB76E] mb-4">Richiesta Inviata!</h3>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          Ti ringraziamo per la richiesta! I nostri consulenti la stanno prendendo in carico e riceverai conferma al piu presto.
        </p>
        <button
          onClick={() => {
            setSubmitStatus(null);
            setCurrentStep(1);
            setFormData({
              contraente: '', pivaCfContraente: '', referenteContatto: '', emailComunicazioni: '', telefonoContatto: '',
              tipologiaTirocinio: '', tipologiaTirocinioAltro: '', tirocinanteCognomeNome: '', tirocinanteCodiceFiscale: '',
              tirocinanteNatoA: '', tirocinanteDataNascita: '', tirocinanteResidenza: '',
              aziendaDenominazione: '', aziendaSede: '', aziendaSettore: '', aziendaPiva: '',
              tipologiaMansione: '', descrizioneAttivita: '', dataInizio: '', dataFine: '',
    consensoPrivacy: false, visuraEnte: null, docIdentita: null
            });
          }}
          className="bg-[#9B59B6] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#9B59B6]/90 transition-all"
        >
          Nuova Richiesta
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {renderStepIndicator()}
      
      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          
          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
              <AlertCircle className="text-red-500" size={20} />
              <span className="text-red-700">Si e verificato un errore. Riprova piu tardi.</span>
            </div>
          )}
          
          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                currentStep === 1 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ChevronLeft size={20} />
              <span>Indietro</span>
            </button>
            
            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={nextStep}
                className="flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-[#9B59B6] to-[#8E44AD] text-white hover:shadow-lg hover:scale-105 transition-all"
              >
                <span>Avanti</span>
                <ChevronRight size={20} />
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center space-x-2 px-8 py-3 rounded-lg font-semibold bg-gradient-to-r from-[#5FB76E] to-[#3AAFA9] text-white hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Invio in corso...</span>
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    <span>Invia Richiesta</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default TirociniForm;
